import React, { useState, useMemo, useRef, useEffect } from 'react';

/**
 * SearchableSelect - A reusable component for selecting a single item from a large list with search.
 * 
 * @param {string} label - Display label for the field
 * @param {Array} options - Array of { value, label, subtitle } objects
 * @param {any} value - Currently selected value
 * @param {Function} onChange - Callback function when selection changes (receives value)
 * @param {string} error - Error message to display
 * @param {boolean} loading - Loading state
 * @param {string} placeholder - Search input placeholder
 * @param {boolean} required - Whether the field is required
 */
const SearchableSelect = ({
  label,
  options = [],
  value,
  onChange,
  error,
  loading = false,
  placeholder = "Search and select...",
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Find label for currently selected value
  const selectedOption = useMemo(() => 
    options.find(opt => String(opt.value) === String(value)),
  [options, value]);

  // Sync search term with selected value when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options.slice(0, 50); // Show top 50 if no search
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(lowerSearch) || 
      (opt.subtitle && opt.subtitle.toLowerCase().includes(lowerSearch))
    ).slice(0, 50);
  }, [options, searchTerm]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Styles
  const containerStyle = {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    position: 'relative'
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  };

  const selectBoxStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: `1px solid ${error ? '#ef4444' : 'var(--border)'}`,
    borderRadius: '8px',
    background: 'var(--surface)',
    color: value ? 'var(--text-primary)' : 'var(--text-secondary)',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const dropdownStyle = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 1000,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    maxHeight: '300px',
    display: 'flex',
    flexDirection: 'column'
  };

  const searchInputStyle = {
    padding: '10px 12px',
    border: 'none',
    borderBottom: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-primary)',
    outline: 'none',
    fontSize: '14px'
  };

  const listContainerStyle = {
    overflowY: 'auto',
    flex: 1
  };

  const optionStyle = (isHighlighted) => ({
    padding: '10px 12px',
    cursor: 'pointer',
    background: isHighlighted ? 'var(--background)' : 'transparent',
    borderBottom: '1px solid var(--border-light)',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  });

  return (
    <div style={containerStyle} ref={containerRef}>
      {label && (
        <div style={labelStyle}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </div>
      )}

      <div 
        style={selectBoxStyle} 
        onClick={() => !loading && setIsOpen(!isOpen)}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        >
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </div>

      {isOpen && (
        <div style={dropdownStyle}>
          <input
            type="text"
            autoFocus
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
            onClick={(e) => e.stopPropagation()}
          />
          <div style={listContainerStyle}>
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
            ) : filteredOptions.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>No matches found</div>
            ) : (
              filteredOptions.map(opt => (
                <div 
                  key={opt.value} 
                  style={optionStyle(String(opt.value) === String(value))}
                  onClick={() => handleSelect(opt)}
                >
                  <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>{opt.label}</span>
                  {opt.subtitle && <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{opt.subtitle}</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {error && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '2px' }}>{error}</div>}
    </div>
  );
};

export default SearchableSelect;
